/**
 * RAM — Change Account Title (Wave 10).
 *
 * POST /v3/maint/acct/change-title
 *
 * Retitling an account triggers a signature-card refresh; the customer
 * must sign a new card before the change is finalized downstream.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import { emitDisclosureRedeliveryRequired } from '../disclosureTriggers';
import type { RamTarget } from '../types';

export interface ChangeTitleProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function ChangeTitle({
  target,
  onDone,
  onCancel,
}: ChangeTitleProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [newTitle, setNewTitle] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const canSave = !submitting && Boolean(target.acctId) && newTitle.trim().length > 0;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitChangeTitle(b),
      {
        accountNumber: target.acctId,
        newTitle: newTitle.trim(),
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    // Reg DD §1030.4 — sig-card refresh required when title changes.
    void emitDisclosureRedeliveryRequired(client, {
      action: 'change-title',
      cifId: target.cifId,
      accountNumber: target.acctId,
      actorUpn: user?.upn,
      payload: { newTitle: newTitle.trim() },
    });
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Account title change submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Change account title</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Retitling triggers a signature card refresh. Customer must sign a new card before the change is finalized.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          New account title
          <input
            className="ram-input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            maxLength={64}
            placeholder="e.g., John Q. Smith and Mary J. Smith JTWROS"
          />
        </label>
        <label className="ram-search__label">
          Reason / supporting context
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={512}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit title change'}
        </Button>
      </div>
    </div>
  );
}
